import React, { useMemo } from 'react';
import { ConnectionsScorecard, CONNECTIONS_THEMES, THEME_DISPLAY_NAMES, Theme } from '../types';
import { HighchartsChart } from '../charts/HighchartsChart';
import { useTheme } from 'src/components/context/ThemeContextProvider';

interface Props {
  scorecards: ConnectionsScorecard[];
  periodLabel: string;
}

interface GapEntry {
  label: string;
  gap: number;
  status: string;
  value: number;
  sample: number | null;
  population: number | null;
  min: number | null;
  max: number | null;
  mid: number | null;
}

export const ExpectedRangeGap: React.FC<Props> = ({ scorecards, periodLabel }) => {
  const { isDarkMode } = useTheme();
  const textColor = isDarkMode ? '#e8ecff' : '#1a2236';
  const options = useMemo(() => {
    const entries: GapEntry[] = [];

    scorecards.forEach((sc) => {
      sc.themes.forEach((t) => {
        if (t.metricValue == null || t.benchmarkMin == null || t.benchmarkMax == null) return;
        const mid = (t.benchmarkMin + t.benchmarkMax) / 2;
        const gap = Math.round((t.metricValue - mid) * 10000) / 100;
        const themeName = THEME_DISPLAY_NAMES[t.theme.toUpperCase() as Theme] || t.theme;
        entries.push({
          label: `${sc.displayName || sc.leaderLogin} - ${themeName}`,
          gap,
          status: t.rangeIndicator || 'within',
          value: Math.round(t.metricValue * 10000) / 100,
          sample: t.sampleSize,
          population: t.populationSize,
          min: Math.round(t.benchmarkMin * 10000) / 100,
          max: Math.round(t.benchmarkMax * 10000) / 100,
          mid: Math.round(mid * 10000) / 100
        });
      });
    });

    entries.sort((a, b) => a.gap - b.gap);

    return {
      chart: { type: 'bar', height: Math.max(300, entries.length * 22 + 80) },
      xAxis: { categories: entries.map((e) => e.label), labels: { style: { fontSize: '11px', color: textColor, fontWeight: '600' } } },
      yAxis: {
        title: { text: 'Gap from Expected Range Midpoint (pp)' },
        plotLines: [{ value: 0, color: '#888', width: 1, dashStyle: 'Dash' }]
      },
      legend: { enabled: false },
      plotOptions: {
        bar: {
          borderRadius: 2,
          pointWidth: 14,
          dataLabels: {
            enabled: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter: function (this: any) {
              const v = this.y;
              return `${v >= 0 ? '+' : ''}${v.toFixed(1)} pp`;
            },
            style: { fontSize: '10px', fontWeight: '600', textOutline: 'none', color: textColor },
            inside: false
          }
        }
      },
      tooltip: {
        useHTML: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: function (this: any) {
          const e = entries[this.point.index];
          if (!e) return '';
          const sampleText = e.sample != null ? `<br/>Sample: ${e.sample}${e.population != null ? ` of ${e.population}` : ''}` : '';
          return `${e.label}<br/>Value: ${e.value}%${sampleText}<br/>Expected Range: ${e.min}%-${e.max}% (midpoint ${e.mid}%)<br/>Gap: ${
            e.gap >= 0 ? '+' : ''
          }${e.gap.toFixed(1)} pp ${e.gap >= 0 ? 'above' : 'below'} midpoint`;
        }
      },
      series: [
        {
          name: 'Gap',
          data: entries.map((e) => ({
            y: e.gap,
            color: e.status === 'above' ? '#1e7d32' : e.status === 'below' ? '#c62828' : '#b76e00'
          }))
        }
      ]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;
  }, [scorecards, textColor]);

  if (!scorecards.length) return null;
  return <HighchartsChart options={options} height={Math.max(300, scorecards.length * 4 * 22 + 80)} />;
};
